<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'FieldsetContractService',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    item: {
      type: Object,
      required: true,
    },
    labelAlign: {
      type: String,
      required: false,
      default: 'right',
    },
    readonly: {
      type: Boolean,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const billingCategories = ref(null);
  const services = ref(null);
  const invoiceGroups = ref(null);
  const ux = ref([]);
  const b_3943151158818434_multiple = computed(() => false);

  const $data = computed(() => ({
    billingCategories: billingCategories.value,
    services: services.value,
    invoiceGroups: invoiceGroups.value,
    ux: ux.value,
    b_3943151158818434_multiple: b_3943151158818434_multiple.value,
  }));

  const $$load_billingCategories = async () => {
    return $fn.value.fetch('get_tags', {
      parameters: { tag_type: 'billing_category' },
    });
  };
  const $$load_services = async () => {
    return $fn.value
      .fetch('suggest_product', {
        parameters: {
          query: '*',
          top: 50,
          skip: 0,
          product_types: 'service',
          product_invoice_groups: 'all',
        },
      })
      .then((data) => _.orderBy(data, [(p) => p.name.toLowerCase()]));
  };
  const $$load_invoiceGroups = async () => {
    return $fn.value.fetch('list_product_invoice_group');
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'billingCategories' },
      { name: 'services' },
      { name: 'invoiceGroups' },
      { name: 'ux' },
      { name: 'b_3943151158818434_multiple', nodeId: '3943151158818434' },
    ],
    loaders: {
      billingCategories: $$load_billingCategories,
      services: $$load_services,
      invoiceGroups: $$load_invoiceGroups,
    },
    ux: {},
    dataRefs: {
      billingCategories,
      services,
      invoiceGroups,
      ux,
      b_3943151158818434_multiple,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.item,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('item');
    }
  );
  watch(
    () => props.labelAlign,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('labelAlign');
    }
  );
  watch(
    () => props.readonly,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('readonly');
    }
  );

  const act_6466705138206980_setData_wknae0dp8_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'item.name',
      value: $event.data.option.name,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_6466705138206980_setData_w88gkig7r_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'item.service_id',
      value: $event.data.option.id,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_6466705138206980_setData_1rxcdwp28_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'feeItem.unit',
      value: $event.data.option.unit,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };

  const act_7315092382398562_reloadComponentData_2wgvditkn =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_v4r0jng0z =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_2wgvditkn,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_v4r0jng0z],
    event: 'reload',
  });
  const act_6466705138206980_setData_wknae0dp8 = new actionTypes.SetDataAction({
    actionArgs: act_6466705138206980_setData_wknae0dp8_getActionArgs,
  });
  const inst_evh_6466705138206980_optionSelected_k0hrmdtj2 =
    new actionTypes.EventHandlerImpl({
      action: act_6466705138206980_setData_wknae0dp8,
      event: 'optionSelected',
      displayName: 'setData',
    });
  const act_6466705138206980_setData_w88gkig7r = new actionTypes.SetDataAction({
    actionArgs: act_6466705138206980_setData_w88gkig7r_getActionArgs,
  });
  const inst_evh_6466705138206980_optionSelected_3mf5q7aqz =
    new actionTypes.EventHandlerImpl({
      action: act_6466705138206980_setData_w88gkig7r,
      event: 'optionSelected',
      displayName: 'setData',
    });
  const act_6466705138206980_setData_1rxcdwp28 = new actionTypes.SetDataAction({
    actionArgs: act_6466705138206980_setData_1rxcdwp28_getActionArgs,
  });
  const inst_evh_6466705138206980_optionSelected_d9e558w7i =
    new actionTypes.EventHandlerImpl({
      action: act_6466705138206980_setData_1rxcdwp28,
      event: 'optionSelected',
      displayName: 'setData',
    });
  const inst_evh_6466705138206980_optionSelected =
    new actionTypes.EventHandlerGroup({
      handlers: [
        inst_evh_6466705138206980_optionSelected_k0hrmdtj2,
        inst_evh_6466705138206980_optionSelected_3mf5q7aqz,
        inst_evh_6466705138206980_optionSelected_d9e558w7i,
      ],
      event: 'optionSelected',
    });

  const actions = {
    inst_evh_7315092382398562_reload_v4r0jng0z,
    inst_evh_7315092382398562_reload,
    inst_evh_6466705138206980_optionSelected_k0hrmdtj2,
    inst_evh_6466705138206980_optionSelected_3mf5q7aqz,
    inst_evh_6466705138206980_optionSelected_d9e558w7i,
    inst_evh_6466705138206980_optionSelected,
  };

  const evh_7315092382398562_reload_v4r0jng0z = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_v4r0jng0z.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_optionSelected_k0hrmdtj2 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_6466705138206980_optionSelected_k0hrmdtj2.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_optionSelected_3mf5q7aqz = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_6466705138206980_optionSelected_3mf5q7aqz.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_optionSelected_d9e558w7i = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_6466705138206980_optionSelected_d9e558w7i.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_optionSelected = async (event, scope) => {
    return await actions.inst_evh_6466705138206980_optionSelected.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <fieldset>
      <ItpFormGroup
        name="service"
        :label="pgettext('ContractService', 'Service')"
        :label-cols-md="3"
        :content-cols-md="9"
        :labelAlign="labelAlign"
        :helpTooltip="
          pgettext(
            'ContractService|Tooltip',
            'Select the product or service the invoice line refers to.'
          )
        "
        required
      >
        <ItpSelect2
          name="client"
          v-model="item.service"
          :value="item.service"
          @input="
            !($event && typeof $event.stopPropagation === 'function') &&
            (item.service =
              $event && typeof $event === 'object' && 'data' in $event
                ? $event.data
                : $event)
          "
          bind-object
          :options="services"
          textField="name"
          :readonly="readonly"
          @optionSelected="evh_6466705138206980_optionSelected($event, {})"
        >
        </ItpSelect2>
      </ItpFormGroup>
      <ItpFormGroup
        :label-cols-md="3"
        :content-cols-md="9"
        name="invoice_group_id"
        :label="pgettext('ContractService', 'Invoice Group')"
        :labelAlign="labelAlign"
        :helpTooltip="
          pgettext(
            'ContractService|Tooltip',
            'The invoice group this service belongs to.'
          )
        "
      >
        <ItpSelect2
          name="invoice_group_id"
          v-model="item.invoice_group_id"
          :value="item.invoice_group_id"
          @input="
            !($event && typeof $event.stopPropagation === 'function') &&
            (item.invoice_group_id =
              $event && typeof $event === 'object' && 'data' in $event
                ? $event.data
                : $event)
          "
          :multiple="b_3943151158818434_multiple"
          :options="invoiceGroups"
          valueField="id"
          textField="name"
          clearable
          :readonly="readonly"
        >
        </ItpSelect2>
      </ItpFormGroup>
      <ItpFormGroup
        :label-cols-md="3"
        name="intermediated_service"
        :label="pgettext('ContractService', 'Intermediated service')"
        :labelAlign="labelAlign"
        :helpTooltip="
          pgettext(
            'ContractService|Tooltip',
            'Select this option if the service is provided via an intermediary party.'
          )
        "
      >
        <ItpFormCheckbox
          name="intermediated_service"
          v-model="item.intermediated_service"
          :value="item.intermediated_service"
          @input="
            !($event && typeof $event.stopPropagation === 'function') &&
            (item.intermediated_service =
              $event && typeof $event === 'object' && 'data' in $event
                ? $event.data
                : $event)
          "
          :readonly="readonly"
        >
        </ItpFormCheckbox>
      </ItpFormGroup>
      <ItpFormGroup
        name="valid_from"
        :label="pgettext('ContractService', 'Valid from')"
        :label-cols-md="3"
        :content-cols-md="4"
        :labelAlign="labelAlign"
        :helpTooltip="
          pgettext(
            'ContractService|Tooltip',
            'Specifies the start date of the service period.'
          )
        "
      >
        <ItpFormDatetime
          name="valid_from"
          v-model="item.valid_from"
          required
          :readonly="readonly"
        >
        </ItpFormDatetime>
      </ItpFormGroup>
      <ItpFormGroup
        name="valid_to"
        :label="pgettext('ContractService', 'Valid to')"
        :label-cols-md="3"
        :content-cols-md="4"
        :labelAlign="labelAlign"
        :helpTooltip="
          pgettext(
            'ContractService|Tooltip',
            'The service end date. Set only if it\'s different from the contract\'s end date.'
          )
        "
      >
        <ItpFormDatetime
          name="valid_to"
          v-model="item.valid_to"
          :readonly="readonly"
        >
        </ItpFormDatetime>
      </ItpFormGroup>
      <ItpFormGroup
        name="valid_to"
        :label="pgettext('ContractService', 'Description')"
        :label-cols-md="3"
        :content-cols-md="9"
        :labelAlign="labelAlign"
        :helpTooltip="
          pgettext(
            'ContractService|Tooltip',
            'Internal notes for reference only. Not visible externally.'
          )
        "
      >
        <ItpFormTextarea
          name="description"
          v-model="item.description"
          :value="item.description"
          @input="
            !($event && typeof $event.stopPropagation === 'function') &&
            (item.description =
              $event && typeof $event === 'object' && 'data' in $event
                ? $event.data
                : $event)
          "
          :readonly="readonly"
          :rows="5"
        >
        </ItpFormTextarea>
      </ItpFormGroup>
    </fieldset>
    <hr />
    <fieldset>
      <legend>
        {{ pgettext('ContractServiceEditor|Section', 'Invoicing') }}
      </legend>
      <ItpFormGroup
        :label-cols-md="3"
        :content-cols-md="6"
        name="billing_category"
        :label="pgettext('ContractService', 'Billing category')"
        :labelAlign="labelAlign"
        :helpTooltip="
          pgettext(
            'ContractService|Help|BillingCategory',
            'The invoice category this service should be assigned to when billed. New values entered will be saved for future use.'
          )
        "
      >
        <ItpSelect2
          name="billing_category"
          taggable
          :options="billingCategories"
          v-model="item.billing_category"
          :value="item.billing_category"
          @input="
            !($event && typeof $event.stopPropagation === 'function') &&
            (item.billing_category =
              $event && typeof $event === 'object' && 'data' in $event
                ? $event.data
                : $event)
          "
          :readonly="item.readonly"
          valueField="name"
          textField="name"
        >
        </ItpSelect2>
      </ItpFormGroup>
      <ItpFormGroup
        name="name"
        :label="pgettext('ContractService', 'Name')"
        :label-cols-md="3"
        :content-cols-md="9"
        :labelAlign="labelAlign"
        :helpTooltip="
          pgettext(
            'ContractService|Tooltip',
            'This text will appear on the invice line.'
          )
        "
        required
      >
        <ItpFormInput
          name="name"
          v-model="item.name"
          :value="item.name"
          @input="
            !($event && typeof $event.stopPropagation === 'function') &&
            (item.name =
              $event && typeof $event === 'object' && 'data' in $event
                ? $event.data
                : $event)
          "
          required
          :readonly="readonly"
        >
        </ItpFormInput>
      </ItpFormGroup>
      <ItpFormGroup
        name="name"
        :label="pgettext('ContractService', 'Name(s)')"
        :label-cols-md="3"
        :content-cols-md="9"
        :labelAlign="labelAlign"
        :helpTooltip="
          pgettext(
            'ContractService|Tooltip',
            'This text will appear on the invice line.'
          )
        "
        required
      >
        <UCContractServiceItemNameEditor
          v-model="item.invoice_item_name"
          :value="item.invoice_item_name"
          @input="
            !($event && typeof $event.stopPropagation === 'function') &&
            (item.invoice_item_name =
              $event && typeof $event === 'object' && 'data' in $event
                ? $event.data
                : $event)
          "
          :readonly="readonly"
        >
        </UCContractServiceItemNameEditor>
      </ItpFormGroup>
      <ItpFormGroup :label-cols-md="3" :content-cols-md="9">
        <hr class="dots" />
      </ItpFormGroup>
      <ItpFormGroup
        name="invoice_notes"
        :label="pgettext('ContractServiceFee', 'Invoice notes')"
        :label-cols-md="3"
        :content-cols-md="9"
        :labelAlign="labelAlign"
        :helpTooltip="
          pgettext(
            'ContractService|Tooltip',
            'Note to display on the invoice line.'
          )
        "
      >
        <ItpFormTextarea
          name="fee_notes"
          v-model="item.invoice_notes"
          :value="item.invoice_notes"
          @input="
            !($event && typeof $event.stopPropagation === 'function') &&
            (item.invoice_notes =
              $event && typeof $event === 'object' && 'data' in $event
                ? $event.data
                : $event)
          "
          :readonly="readonly"
        >
        </ItpFormTextarea>
      </ItpFormGroup>
      <ItpFormGroup
        name="billing_frequency"
        :label="pgettext('ContractService', 'Billing frequency')"
        :label-cols-md="3"
        :content-cols-md="3"
        :labelAlign="labelAlign"
        :helpTooltip="
          pgettext(
            'ContractService|Tooltip',
            'The cycle length of issuing the invoices.'
          )
        "
      >
        <BillingFrequencySelector
          v-model="item.billing_frequency"
          :value="item.billing_frequency"
          @input="
            !($event && typeof $event.stopPropagation === 'function') &&
            (item.billing_frequency =
              $event && typeof $event === 'object' && 'data' in $event
                ? $event.data
                : $event)
          "
          :readonly="readonly"
        >
        </BillingFrequencySelector>
      </ItpFormGroup>
      <ItpFormGroup
        name="billing_date_rule"
        :label="pgettext('ContractService', 'Billing date rule')"
        :label-cols-md="3"
        :content-cols-md="9"
        :labelAlign="labelAlign"
        :helpTooltip="
          pgettext(
            'ContractService|Tooltip',
            'The cycle length of issuing the invoices.'
          )
        "
      >
        <BillingDateRuleFields
          v-model="item.billing_date_rule"
          :value="item.billing_date_rule"
          @input="
            !($event && typeof $event.stopPropagation === 'function') &&
            (item.billing_date_rule =
              $event && typeof $event === 'object' && 'data' in $event
                ? $event.data
                : $event)
          "
          :readonly="readonly"
        >
        </BillingDateRuleFields>
      </ItpFormGroup>
      <ItpFormGroup :label-cols-md="3" :content-cols-md="9">
        <hr class="dots" />
      </ItpFormGroup>
      <ItpFormGroup
        name="due_date_rule"
        :label="pgettext('ContractService', 'Due date rule')"
        :label-cols-md="3"
        :content-cols-md="9"
        :labelAlign="labelAlign"
        :helpTooltip="
          pgettext(
            'ContractService|Tooltip',
            'The rule of calculating the payment dates.'
          )
        "
      >
        <DueDateRuleFields
          v-model="item.due_date_rule"
          :value="item.due_date_rule"
          @input="
            !($event && typeof $event.stopPropagation === 'function') &&
            (item.due_date_rule =
              $event && typeof $event === 'object' && 'data' in $event
                ? $event.data
                : $event)
          "
          :readonly="readonly"
        >
        </DueDateRuleFields>
      </ItpFormGroup>
      <ItpFormGroup :label-cols-md="3" :content-cols-md="9">
        <hr class="dots" />
      </ItpFormGroup>
      <ItpFormGroup
        name="billing_currency"
        :label="pgettext('ContractService', 'Billing currency')"
        :label-cols-md="3"
        :content-cols-md="3"
        :labelAlign="labelAlign"
        :helpTooltip="
          pgettext(
            'ContractService|Tooltip',
            'The currency to use on the invoice.'
          )
        "
      >
        <BillingCurrencySelector
          v-model="item.billing_currency"
          :value="item.billing_currency"
          @input="
            !($event && typeof $event.stopPropagation === 'function') &&
            (item.billing_currency =
              $event && typeof $event === 'object' && 'data' in $event
                ? $event.data
                : $event)
          "
          :readonly="readonly"
        >
        </BillingCurrencySelector>
      </ItpFormGroup>
      <ItpFormGroup
        name="exchange_rate_source"
        :label="pgettext('ContractService', 'Exchange rate source')"
        :label-cols-md="3"
        :content-cols-md="3"
        :labelAlign="labelAlign"
        :helpTooltip="
          pgettext(
            'ContractService|Tooltip',
            'The currency exchange rate\'s provider.'
          )
        "
      >
        <ExchangeRateSourceSelector
          v-model="item.exchange_rate_source"
          :value="item.exchange_rate_source"
          @input="
            !($event && typeof $event.stopPropagation === 'function') &&
            (item.exchange_rate_source =
              $event && typeof $event === 'object' && 'data' in $event
                ? $event.data
                : $event)
          "
          :readonly="readonly"
        >
        </ExchangeRateSourceSelector>
      </ItpFormGroup>
      <ItpFormGroup
        name="exchange_rate_source_spec"
        :label="
          pgettext('ContractService', 'Exchange rate source specification')
        "
        :label-cols-md="3"
        :content-cols-md="9"
        :labelAlign="labelAlign"
        :helpTooltip="
          pgettext(
            'ContractService|Tooltip',
            'The currency exchange rate configuration.'
          )
        "
      >
        <ExchangeRateSourceSpecEditor
          v-model="item.exchange_rate_source_spec"
          :value="item.exchange_rate_source_spec"
          @input="
            !($event && typeof $event.stopPropagation === 'function') &&
            (item.exchange_rate_source_spec =
              $event && typeof $event === 'object' && 'data' in $event
                ? $event.data
                : $event)
          "
          :readonly="readonly"
        >
        </ExchangeRateSourceSpecEditor>
      </ItpFormGroup>
      <ItpFormGroup
        name="exchange_rate_date_rule"
        :label="pgettext('ContractService', 'Exchange rate date rule')"
        :label-cols-md="3"
        :content-cols-md="9"
        :labelAlign="labelAlign"
        :helpTooltip="
          pgettext(
            'ContractService|Tooltip',
            'The date used to determine the exchange rate for billing purposes.'
          )
        "
      >
        <ExchangeRateDateRuleFields
          v-model="item.exchange_rate_date_rule"
          :value="item.exchange_rate_date_rule"
          @input="
            !($event && typeof $event.stopPropagation === 'function') &&
            (item.exchange_rate_date_rule =
              $event && typeof $event === 'object' && 'data' in $event
                ? $event.data
                : $event)
          "
          :readonly="readonly"
        >
        </ExchangeRateDateRuleFields>
      </ItpFormGroup>
    </fieldset>
  </ItpUsercontrol>
</template>
